import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, Button } from '../../../pages/DesignSystem';
import { Event } from '../../../types/event.types';
import { supabaseEventSeriesService } from '../../../supabase/eventSeries.service';
import { formatEventDateTime } from '../../../utils/timezoneUtils';
import toast from 'react-hot-toast';
import './OccurrenceEditModal.css';

interface OccurrenceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event | null;
  onSuccess?: () => void;
}

/**
 * OccurrenceEditModal - Simplified modal for editing only the date/time
 * of a single occurrence in a recurring series.
 */
const OccurrenceEditModal: React.FC<OccurrenceEditModalProps> = ({
  isOpen,
  onClose,
  event,
  onSuccess
}) => {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with event data
  useEffect(() => {
    if (event?.datetime && isOpen) {
      const dt = new Date(event.datetime);
      // Format: YYYY-MM-DD
      const dateStr = dt.toISOString().split('T')[0];
      // Format: HH:MM (start time from datetime)
      const startTimeStr = dt.toTimeString().slice(0, 5);
      setDate(dateStr);
      setStartTime(startTimeStr);

      // End time - extract from endTime field if exists
      if (event.endTime) {
        const endDt = new Date(event.endTime);
        const endTimeStr = endDt.toTimeString().slice(0, 5);
        setEndTime(endTimeStr);
      } else {
        setEndTime('');
      }
    }
  }, [event, isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, isSubmitting]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!event?.id || !date || !startTime) {
      toast.error('Please fill in date and start time');
      return;
    }

    // Validate end time is after start time if provided
    if (endTime && endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create new datetime from date and start time
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = startTime.split(':').map(Number);
      const newDatetime = new Date(year, month - 1, day, hours, minutes);

      // Create end time ISO string if provided
      let newEndTime: Date | undefined;
      if (endTime) {
        const [endHours, endMinutes] = endTime.split(':').map(Number);
        newEndTime = new Date(year, month - 1, day, endHours, endMinutes);
      }

      // Update datetime, end time and mark as exception
      await supabaseEventSeriesService.updateSingleEvent(event.id, {
        datetime: newDatetime,
        endTime: newEndTime,
        isException: true
      });

      toast.success('Date and time updated successfully!', {
        duration: 3000,
        style: {
          borderRadius: '12px',
          background: 'linear-gradient(to bottom, #E8F5E8, #F0FFF0)',
          color: '#2E7D2E',
        }
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating occurrence:', error);
      toast.error('Failed to update. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get original date for comparison
  const originalDateFormatted = event?.datetime
    ? formatEventDateTime(event.datetime, event.timezone)
    : { date: '', time: '', weekday: '' };

  return (
    <AnimatePresence>
      {isOpen && event && (
        <motion.div
          className="occurrence-edit-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            className="occurrence-edit-modal__dialog"
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="occurrence-edit-modal__header">
              <div className="occurrence-edit-modal__header-content">
                <Icon name="Calendar" size={24} className="occurrence-edit-modal__icon" />
                <div>
                  <h2 className="occurrence-edit-modal__title">Edit Occurrence</h2>
                  <p className="occurrence-edit-modal__subtitle">{event.title}</p>
                </div>
              </div>
              <button
                type="button"
                className="occurrence-edit-modal__close"
                onClick={onClose}
                disabled={isSubmitting}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="occurrence-edit-modal__original">
              <span className="occurrence-edit-modal__original-label">Current:</span>
              <span className="occurrence-edit-modal__original-value">
                {originalDateFormatted.date} at {originalDateFormatted.time}
              </span>
            </div>

            <form onSubmit={handleSubmit} className="occurrence-edit-modal__form">
              <div className="occurrence-edit-modal__field">
                <label htmlFor="occurrence-date" className="occurrence-edit-modal__label">
                  <Icon name="Calendar" size={16} />
                  New Date
                </label>
                <input
                  id="occurrence-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="occurrence-edit-modal__input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="occurrence-edit-modal__time-row">
                <div className="occurrence-edit-modal__field occurrence-edit-modal__field--half">
                  <label htmlFor="occurrence-start-time" className="occurrence-edit-modal__label">
                    <Icon name="Clock" size={16} />
                    Start Time
                  </label>
                  <input
                    id="occurrence-start-time"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="occurrence-edit-modal__input"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="occurrence-edit-modal__field occurrence-edit-modal__field--half">
                  <label htmlFor="occurrence-end-time" className="occurrence-edit-modal__label">
                    <Icon name="Clock" size={16} />
                    End Time
                  </label>
                  <input
                    id="occurrence-end-time"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="occurrence-edit-modal__input"
                    disabled={isSubmitting}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <p className="occurrence-edit-modal__note">
                <Icon name="Info" size={14} />
                This will only change this specific occurrence. Other events in the series will not be affected.
              </p>

              <div className="occurrence-edit-modal__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OccurrenceEditModal;
